import { useEffect, useMemo, useState, type ReactNode } from "react";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { MAIL_TOKEN_LABELS } from "@/lib/templatePlaceholders";
import { Alert } from "@/components/ui/Alert";
import { Card } from "@/components/ui/Card";
import type { ArrivalTypeRecord, PitchCategory } from "@/types/camping";

export type StayHistory = "any" | "first_stay" | "returning";

export interface MailOption {
  id: string;
  label: string;
  description?: string;
}

export interface MailPlaceholder {
  key: string;
  description: string;
}

interface LayoutOption {
  id: string;
  name: string;
}

export interface MailMeta {
  statuses: MailOption[];
  sources: MailOption[];
  guest_types: MailOption[];
  layouts: LayoutOption[];
}

export interface MailConstraints {
  statuses: string[];
  pitch_category_ids: string[];
  arrival_types: string[];
  guest_types: string[];
  country_codes: string[];
  sources: string[];
  pitch_codes: string[];
  min_nights: number | null;
  max_nights: number | null;
  min_persons: number | null;
  max_persons: number | null;
  min_adults: number | null;
  max_adults: number | null;
  min_children: number | null;
  max_children: number | null;
  min_dogs: number | null;
  max_dogs: number | null;
  has_children: boolean | null;
  has_dogs: boolean | null;
  wants_electricity: boolean | null;
  has_vehicle: boolean | null;
  pitch_assigned: boolean | null;
  stay_history: StayHistory;
  exclude_place_banned: boolean;
  price_pending: boolean | null;
}

export interface MailPreviewRecipient {
  booking_id: string;
  booking_number: string;
  guest_name: string;
  email: string;
  arrival_date: string;
  departure_date: string;
  status: string;
  pitch_code: string | null;
}

export interface MailPreview {
  recipient_count: number;
  skipped_without_email: number;
  skipped_duplicate_email: number;
  truncated: boolean;
  applied_statuses: string[];
  recipients: MailPreviewRecipient[];
  sample_to: string | null;
  sample_subject: string | null;
  sample_body_text: string | null;
}

export const TEXT_STARTERS: { id: string; label: string; hint: string; subject: string; body: string }[] = [
  {
    id: "info",
    label: "Kurze Info",
    hint: "Leerer Text zum Selbstschreiben",
    subject: "Nachricht von {{company_name}}",
    body: "{{salutation}},\n\nwir möchten Sie kurz informieren:\n\n",
  },
  {
    id: "welcome",
    label: "Willkommen",
    hint: "Zur Anreise",
    subject: "Ihre Anreise am {{arrival_date}}",
    body: "{{salutation}},\n\nwir freuen uns auf Ihre Anreise am {{arrival_date}}.\n\nBuchung: {{booking_number}}\nPlatz: {{location}}\n\nBis bald auf {{company_name}}.",
  },
  {
    id: "departure",
    label: "Gute Heimreise",
    hint: "Zur Abreise",
    subject: "Ihre Abreise am {{departure_date}}",
    body: "{{salutation}},\n\nIhr Aufenthalt endet am {{departure_date}}.\n\nWir wünschen eine gute Heimreise.",
  },
];

const PLACEHOLDER_GROUPS: Record<string, string> = {
  salutation: "Häufig",
  guest_first_name: "Häufig",
  guest_name: "Häufig",
  arrival_date: "Häufig",
  departure_date: "Häufig",
  pitch_label: "Häufig",
  booking_number: "Häufig",
  company_name: "Häufig",
  invoice_number: "Häufig",
  total_amount: "Häufig",
  open_amount: "Häufig",
  due_date: "Häufig",
  issue_date: "Häufig",
  reminder_level: "Häufig",
  nights: "Aufenthalt",
  location: "Aufenthalt",
  category_name: "Aufenthalt",
  days_until_arrival: "Aufenthalt",
  days_until_departure: "Aufenthalt",
  today: "Aufenthalt",
  check_in_notice: "Aufenthalt",
  adults: "Personen",
  children: "Personen",
  dogs: "Personen",
  persons: "Personen",
  primary_traveler_name: "Personen",
  guest_last_name: "Gast",
  guest_email: "Gast",
  guest_phone: "Gast",
  guest_company: "Gast",
  guest_street: "Gast",
  guest_postal_code: "Gast",
  guest_city: "Gast",
  guest_country: "Gast",
  booking_status: "Buchung",
  vehicle_type: "Buchung",
  vehicle_length_m: "Buchung",
  license_plate: "Buchung",
  stay_price: "Buchung",
  booking_notes: "Buchung",
  booking_details: "Buchung",
  booking_changes: "Buchung",
  price_breakdown: "Buchung",
  button_booking: "Schaltflächen",
  button_shop: "Schaltflächen",
  user_name: "Signatur",
  user_first_name: "Signatur",
  user_email: "Signatur",
};

const GROUP_ORDER = ["Häufig", "Signatur", "Schaltflächen", "Aufenthalt", "Personen", "Gast", "Buchung", "Weitere"];

export function constraintCount(value: MailConstraints): number {
  let count = 0;
  if (value.statuses.length > 0) count += 1;
  if (value.pitch_category_ids.length > 0) count += 1;
  if (value.arrival_types.length > 0) count += 1;
  if (value.guest_types.length > 0) count += 1;
  if (value.country_codes.length > 0) count += 1;
  if (value.sources.length > 0) count += 1;
  if (value.pitch_codes.length > 0) count += 1;
  if (value.min_nights != null || value.max_nights != null) count += 1;
  if (value.min_persons != null || value.max_persons != null) count += 1;
  if (value.min_adults != null || value.max_adults != null) count += 1;
  if (value.min_children != null || value.max_children != null) count += 1;
  if (value.min_dogs != null || value.max_dogs != null) count += 1;
  if (value.has_children != null) count += 1;
  if (value.has_dogs != null) count += 1;
  if (value.wants_electricity != null) count += 1;
  if (value.has_vehicle != null) count += 1;
  if (value.pitch_assigned != null) count += 1;
  if (value.stay_history !== "any") count += 1;
  if (!value.exclude_place_banned) count += 1;
  if (value.price_pending != null) count += 1;
  return count;
}

export function timingFieldCopy(timing: string): {
  date: string;
  from: string;
  to: string;
  days: string;
  daysHint: string;
} {
  if (timing.startsWith("depart")) {
    return {
      date: "Abreisetag",
      from: "Abreise von",
      to: "Abreise bis",
      days: "Wie viele Tage ab heute?",
      daysHint: "7 bedeutet: Abreise heute oder in den nächsten 7 Tagen.",
    };
  }
  if (timing.startsWith("birthday")) {
    return {
      date: "Geburtstag",
      from: "Von",
      to: "Bis",
      days: "Wie viele Tage ab heute?",
      daysHint: "7 bedeutet: Geburtstag heute oder in den nächsten 7 Tagen.",
    };
  }
  if (timing.startsWith("on_site")) {
    return {
      date: "Tag am Platz",
      from: "Vor Ort von",
      to: "Vor Ort bis",
      days: "Wie viele Tage ab heute?",
      daysHint: "Beide Tage zählen mit.",
    };
  }
  if (timing.startsWith("checked_out")) {
    return {
      date: "Abreisetag",
      from: "Abgereist von",
      to: "Abgereist bis",
      days: "Wie viele Tage ab heute?",
      daysHint: "",
    };
  }
  if (timing.startsWith("created")) {
    return {
      date: "Tag",
      from: "Buchung angelegt von",
      to: "Buchung angelegt bis",
      days: "Wie viele Tage ab heute?",
      daysHint: "",
    };
  }
  return {
    date: "Anreisetag",
    from: "Anreise von",
    to: "Anreise bis",
    days: "Wie viele Tage ab heute?",
    daysHint: "7 bedeutet: Anreise heute oder in den nächsten 7 Tagen.",
  };
}

export function offsetCopy(offset: string | null | undefined): { label: string; hint: string } {
  switch (offset) {
    case "stay_day":
      return {
        label: "Welcher Tag des Aufenthalts?",
        hint: "Tag 1 ist der Anreisetag. Tag 2 ist der Tag danach.",
      };
    case "days_after":
      return { label: "Wie viele Tage danach?", hint: "1 bedeutet: einen Tag später." };
    case "days_after_zero":
      return {
        label: "Wie viele Tage nach dem Anlegen der Buchung?",
        hint: "0 bedeutet: noch am selben Tag.",
      };
    case "days_before":
      return { label: "Wie viele Tage vorher?", hint: "3 bedeutet: drei Tage vor dem Termin." };
    default:
      return { label: "Tage", hint: "" };
  }
}

function toggleId(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

export function ChoiceCard({
  selected,
  title,
  hint,
  onClick,
}: {
  selected: boolean;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={clsx(
        "min-h-[5.25rem] rounded-xl border px-4 py-3 text-left transition",
        selected
          ? "border-accent bg-accent-soft ring-1 ring-accent"
          : "border-separator bg-card hover:bg-fill-tertiary/60",
      )}
    >
      <span className="block text-sm font-semibold text-label">{title}</span>
      <span className="mt-1 block text-xs leading-snug text-label-secondary">{hint}</span>
      {selected ? <span className="mt-2 block text-xs font-semibold text-accent">Ausgewählt</span> : null}
    </button>
  );
}

export function StepHeading({ step, title }: { step: number; title: string }) {
  return (
    <span className="inline-flex items-center gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-label-on-accent">
        {step}
      </span>
      {title}
    </span>
  );
}

export function DisclosureButton({
  open,
  title,
  hint,
  onClick,
}: {
  open: boolean;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center justify-between gap-3 rounded-xl border border-dashed border-separator px-4 py-3 text-left hover:bg-fill-tertiary/40"
      aria-expanded={open}
      onClick={onClick}
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-label">{title}</span>
        <span className="mt-0.5 block text-xs leading-snug text-label-secondary">{hint}</span>
      </span>
      <ChevronDown size={18} className={clsx("shrink-0 text-label-secondary transition", open && "rotate-180")} />
    </button>
  );
}

export function SwitchRow({
  on,
  title,
  hint,
  disabled,
  onToggle,
}: {
  on: boolean;
  title: string;
  hint: string;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 rounded-xl border border-separator bg-card px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-label">{title}</span>
        <span className="mt-0.5 block text-xs text-label-secondary">{hint}</span>
      </span>
      <span
        className={clsx(
          "shrink-0 rounded-full px-3 py-1 text-sm font-semibold",
          on ? "bg-status-green/15 text-status-green" : "bg-fill-tertiary text-label-secondary",
        )}
      >
        {on ? "An" : "Aus"}
      </span>
    </button>
  );
}

export function StatusPill({ status, label }: { status: string; label: string }) {
  const tone =
    status === "sent"
      ? "bg-status-green/15 text-status-green"
      : status === "failed"
        ? "bg-status-red/15 text-status-red"
        : status === "partial" || status === "pending"
          ? "bg-status-orange/15 text-status-orange"
          : "bg-accent-soft text-accent";
  return <span className={clsx("inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium", tone)}>{label}</span>;
}

function NumberField({
  label,
  value,
  onChange,
  min = 0,
  max = 400,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-label-secondary">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        placeholder="egal"
        className="eh-input w-full"
        value={value ?? ""}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(raw === "" ? null : Number(raw));
        }}
      />
    </label>
  );
}

function TriChoice({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (value: boolean | null) => void;
}) {
  const options: { id: string; label: string; next: boolean | null }[] = [
    { id: "any", label: "Egal", next: null },
    { id: "yes", label: "Ja", next: true },
    { id: "no", label: "Nein", next: false },
  ];
  const current = value === null ? "any" : value ? "yes" : "no";
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm text-label">{label}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={current === option.id}
            className={clsx(
              "rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset",
              current === option.id
                ? "bg-accent-soft text-accent ring-accent"
                : "bg-card text-label ring-separator hover:bg-fill-tertiary",
            )}
            onClick={() => onChange(option.next)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function CheckGroup({
  label,
  hint,
  options,
  selected,
  onChange,
}: {
  label: string;
  hint?: string;
  options: MailOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  if (options.length === 0) return null;
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-label">{label}</legend>
      {hint ? <p className="text-xs text-label-secondary">{hint}</p> : null}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const on = selected.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={on}
              className={clsx(
                "rounded-full px-3 py-2 text-sm ring-1 ring-inset",
                on ? "bg-accent-soft font-medium text-accent ring-accent" : "bg-card text-label ring-separator hover:bg-fill-tertiary",
              )}
              onClick={() => onChange(toggleId(selected, option.id))}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function FilterSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/20 p-4">
      <h3 className="text-sm font-semibold text-label">{title}</h3>
      {children}
    </div>
  );
}

export function ConstraintFields({
  value,
  onChange,
  meta,
  categories,
  arrivalTypes,
}: {
  value: MailConstraints;
  onChange: (next: MailConstraints) => void;
  meta: MailMeta | null;
  categories: PitchCategory[];
  arrivalTypes: ArrivalTypeRecord[];
}) {
  const patch = (partial: Partial<MailConstraints>) => onChange({ ...value, ...partial });
  const hasPrecise =
    value.min_nights != null ||
    value.max_nights != null ||
    value.min_persons != null ||
    value.max_persons != null ||
    value.min_adults != null ||
    value.max_adults != null ||
    value.min_children != null ||
    value.max_children != null ||
    value.min_dogs != null ||
    value.max_dogs != null ||
    value.price_pending != null;
  const [preciseOpen, setPreciseOpen] = useState(false);
  useEffect(() => {
    if (hasPrecise) setPreciseOpen(true);
  }, [hasPrecise]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-label-secondary">
        Alles auf „Egal“ lassen heißt: jeder Gast aus der gewählten Gruppe ist dabei. Nur anfassen, wenn die Mail an weniger Leute gehen soll.
      </p>
      <FilterSection title="Alltag">
        <div className="grid gap-4 sm:grid-cols-2">
          <TriChoice label="Mit Kindern" value={value.has_children} onChange={(has_children) => patch({ has_children })} />
          <TriChoice label="Mit Hund" value={value.has_dogs} onChange={(has_dogs) => patch({ has_dogs })} />
          <TriChoice label="Strom gewünscht" value={value.wants_electricity} onChange={(wants_electricity) => patch({ wants_electricity })} />
          <TriChoice label="Fahrzeug erfasst" value={value.has_vehicle} onChange={(has_vehicle) => patch({ has_vehicle })} />
          <TriChoice label="Platz schon zugewiesen" value={value.pitch_assigned} onChange={(pitch_assigned) => patch({ pitch_assigned })} />
          <fieldset>
            <legend className="mb-1.5 text-sm text-label">War der Gast schon einmal da?</legend>
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  ["any", "Egal"],
                  ["first_stay", "Zum ersten Mal"],
                  ["returning", "Schon einmal da"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={value.stay_history === id}
                  className={clsx(
                    "rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset",
                    value.stay_history === id
                      ? "bg-accent-soft text-accent ring-accent"
                      : "bg-card text-label ring-separator hover:bg-fill-tertiary",
                  )}
                  onClick={() => patch({ stay_history: id })}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
        <label className="flex items-start gap-3 rounded-xl border border-separator bg-card px-3 py-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={value.exclude_place_banned}
            onChange={(event) => patch({ exclude_place_banned: event.target.checked })}
          />
          <span>
            <span className="block font-medium text-label">Gäste mit Platzverbot auslassen</span>
            <span className="mt-0.5 block text-xs text-label-secondary">Ist eingeschaltet. Diese Gäste bekommen dann keine Mail.</span>
          </span>
        </label>
      </FilterSection>

      <FilterSection title="Platz, Reise, Herkunft">
        <CheckGroup
          label="Platzkategorie"
          hint="Ohne Auswahl sind alle Kategorien dabei."
          options={categories.map((category) => ({ id: category.id, label: category.name }))}
          selected={value.pitch_category_ids}
          onChange={(pitch_category_ids) => patch({ pitch_category_ids })}
        />
        <CheckGroup
          label="Anreiseart"
          hint="Zum Beispiel Wohnmobil oder Zelt."
          options={arrivalTypes.filter((item) => item.is_active).map((item) => ({ id: item.code, label: item.name }))}
          selected={value.arrival_types}
          onChange={(arrival_types) => patch({ arrival_types })}
        />
        <CheckGroup
          label="Gästetyp"
          options={meta?.guest_types ?? []}
          selected={value.guest_types}
          onChange={(guest_types) => patch({ guest_types })}
        />
        <CheckGroup
          label="Woher kommt die Buchung?"
          options={meta?.sources ?? []}
          selected={value.sources}
          onChange={(sources) => patch({ sources })}
        />
        <CheckGroup
          label="Buchungsstatus"
          hint="Ohne Auswahl gilt der übliche Status der Gruppe, zum Beispiel eingecheckt."
          options={meta?.statuses ?? []}
          selected={value.statuses}
          onChange={(statuses) => patch({ statuses })}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-label">Nur diese Länder</span>
            <span className="mb-1 block text-xs text-label-secondary">Leer lassen für alle. Zum Beispiel DE, NL.</span>
            <input
              className="eh-input w-full"
              placeholder="DE, NL"
              value={value.country_codes.join(", ")}
              onChange={(event) =>
                patch({
                  country_codes: event.target.value
                    .split(/[,;]+/)
                    .map((code) => code.trim().toUpperCase())
                    .filter((code, index, all) => code && all.indexOf(code) === index),
                })
              }
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-label">Nur diese Plätze</span>
            <span className="mb-1 block text-xs text-label-secondary">Leer lassen für alle. Zum Beispiel A12, B3.</span>
            <input
              className="eh-input w-full"
              placeholder="A12, B3"
              value={value.pitch_codes.join(", ")}
              onChange={(event) =>
                patch({
                  pitch_codes: event.target.value
                    .split(/[,;]+/)
                    .map((code) => code.trim())
                    .filter((code, index, all) => code && all.indexOf(code) === index),
                })
              }
            />
          </label>
        </div>
      </FilterSection>

      <DisclosureButton
        open={preciseOpen}
        title="Noch genauer: Nächte, Personen, Preis"
        hint="Nur nötig, wenn die Mail zum Beispiel nur an lange Aufenthalte oder Buchungen ohne Preis gehen soll."
        onClick={() => setPreciseOpen((open) => !open)}
      />
      {preciseOpen ? (
        <FilterSection title="Anzahl und Preis">
          <p className="text-xs text-label-secondary">Leere Felder heißen „egal“.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <NumberField label="Mindestens Nächte" value={value.min_nights} onChange={(min_nights) => patch({ min_nights })} />
            <NumberField label="Höchstens Nächte" value={value.max_nights} onChange={(max_nights) => patch({ max_nights })} />
            <NumberField label="Mindestens Personen" value={value.min_persons} onChange={(min_persons) => patch({ min_persons })} max={99} />
            <NumberField label="Höchstens Personen" value={value.max_persons} onChange={(max_persons) => patch({ max_persons })} max={99} />
            <NumberField label="Mindestens Erwachsene" value={value.min_adults} onChange={(min_adults) => patch({ min_adults })} max={99} />
            <NumberField label="Höchstens Erwachsene" value={value.max_adults} onChange={(max_adults) => patch({ max_adults })} max={99} />
            <NumberField label="Mindestens Kinder" value={value.min_children} onChange={(min_children) => patch({ min_children })} max={99} />
            <NumberField label="Höchstens Kinder" value={value.max_children} onChange={(max_children) => patch({ max_children })} max={99} />
            <NumberField label="Mindestens Hunde" value={value.min_dogs} onChange={(min_dogs) => patch({ min_dogs })} max={20} />
            <NumberField label="Höchstens Hunde" value={value.max_dogs} onChange={(max_dogs) => patch({ max_dogs })} max={20} />
          </div>
          <TriChoice
            label="Nur Buchungen, bei denen der Preis erst später feststand"
            value={value.price_pending}
            onChange={(price_pending) => patch({ price_pending })}
          />
          <p className="text-xs text-label-secondary">
            „Ja“ trifft Buchungen ohne Preis zum Buchungszeitpunkt. Mit dem Wort „Aufenthaltspreis“ steht in der Mail der inzwischen bekannte Preis.
          </p>
        </FilterSection>
      ) : null}
    </div>
  );
}

function groupPlaceholders(placeholders: MailPlaceholder[]) {
  const buckets = new Map<string, { key: string; label: string; description: string }[]>();
  for (const item of placeholders) {
    const group = PLACEHOLDER_GROUPS[item.key] ?? "Weitere";
    const label = MAIL_TOKEN_LABELS[item.key] ?? item.description;
    const list = buckets.get(group) ?? [];
    list.push({ key: item.key, label, description: item.description });
    buckets.set(group, list);
  }
  return GROUP_ORDER.filter((name) => buckets.has(name)).map((name) => ({
    title: name,
    items: buckets.get(name) ?? [],
  }));
}

function PlaceholderChips({
  items,
  onInsert,
}: {
  items: { key: string; label: string; description: string }[];
  onInsert: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          title={item.description}
          aria-label={`${item.label} einfügen`}
          className={
            item.key === "salutation"
              ? "rounded-full bg-teal-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-800"
              : "rounded-full bg-card px-3 py-1.5 text-sm font-medium text-label ring-1 ring-inset ring-separator hover:bg-accent-soft"
          }
          onClick={() => onInsert(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function PlaceholderGroups({
  placeholders,
  onInsert,
  targetLabel,
}: {
  placeholders: MailPlaceholder[];
  onInsert: (key: string) => void;
  targetLabel: string;
}) {
  const groups = useMemo(() => groupPlaceholders(placeholders), [placeholders]);
  const pinned = groups.filter((group) => group.title === "Häufig" || group.title === "Signatur");
  const primary = pinned.length > 0 ? pinned : groups.slice(0, 1);
  const rest = groups.filter((group) => !primary.includes(group));
  const [open, setOpen] = useState(false);
  const hasGreeting = placeholders.some((item) => item.key === "salutation");

  return (
    <div className="space-y-3 rounded-xl bg-fill-tertiary/30 p-3">
      <p className="text-sm text-label-secondary">
        Erst in {targetLabel === "Betreff" ? "den Betreff" : "den Text"} klicken, dann auf ein Wort.
        Beim Versand steht dort der passende Wert, zum Beispiel der Name des Gastes.
        Steht direkt dahinter ein Wort wie Kindern, Nächte oder Erwachsenen, wird bei genau 1 die Einzahl verwendet: 1 Kind, 1 Nacht, 1 Erwachsenem.
        Einen eingesetzten Platzhalter können Sie markieren und fett, kursiv oder unterstrichen setzen.
        {hasGreeting
          ? " Mail-Begrüßung wird zu „Sehr geehrter Herr …“ oder „Sehr geehrte Frau …“. Steht sie im Text, entfällt die Anrede über dem Text."
          : ""}
      </p>
      {primary.map((group) => (
        <div key={group.title} className="space-y-2">
          {group.title === "Signatur" ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{group.title}</p>
          ) : null}
          <PlaceholderChips items={group.items} onInsert={onInsert} />
        </div>
      ))}
      {rest.length > 0 ? (
        <button type="button" className="text-sm font-medium text-accent" onClick={() => setOpen((value) => !value)}>
          {open ? "Weniger Wörter" : "Weitere Wörter anzeigen"}
        </button>
      ) : null}
      {open
        ? rest.map((group) => (
            <div key={group.title} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{group.title}</p>
              <PlaceholderChips items={group.items} onInsert={onInsert} />
            </div>
          ))
        : null}
    </div>
  );
}

export function PreviewPanel({
  preview,
  stale,
  staleHint = "Gruppe oder Text haben sich geändert. Noch einmal auf „Empfänger anzeigen“ tippen, danach können Sie senden.",
  statusLabel,
}: {
  preview: MailPreview | null;
  stale?: boolean;
  staleHint?: string;
  statusLabel: (status: string) => string;
}) {
  if (!preview) return null;
  const count = preview.recipient_count;
  const notes: string[] = [];
  if (preview.skipped_without_email > 0) {
    notes.push(
      preview.skipped_without_email === 1
        ? "1 Buchung hat keine E-Mail-Adresse und wird ausgelassen."
        : `${preview.skipped_without_email} Buchungen haben keine E-Mail-Adresse und werden ausgelassen.`,
    );
  }
  if (preview.skipped_duplicate_email > 0) {
    notes.push(
      preview.skipped_duplicate_email === 1
        ? "1 doppelte E-Mail-Adresse wird nur einmal angeschrieben."
        : `${preview.skipped_duplicate_email} doppelte E-Mail-Adressen werden nur einmal angeschrieben.`,
    );
  }
  if (preview.applied_statuses.length > 0) {
    notes.push(`Status: ${preview.applied_statuses.map(statusLabel).join(", ")}.`);
  }
  if (preview.truncated) notes.push("Es sind mehr als 200 Gäste. Hier sehen Sie die ersten 200.");
  const guestWord = count === 1 ? "Gast bekommt diese Mail" : "Gäste bekommen diese Mail";

  return (
    <div className="space-y-3">
      {stale ? (
        <Alert variant="warning" title="Bitte erneut anzeigen">
          {staleHint}
        </Alert>
      ) : null}
      <Alert variant={count > 0 ? "info" : "warning"} title={count > 0 ? `${count} ${guestWord}` : "Niemand passt auf diese Auswahl"}>
        {count === 0 ? "Ändern Sie die Gruppe oder nehmen Sie Einschränkungen weg." : notes.join(" ")}
      </Alert>
      {preview.recipients.length > 0 ? (
        <div className="eh-table-scroll max-h-80 overflow-auto rounded-xl border border-separator">
          <table className="eh-table text-left text-sm">
            <thead>
              <tr>
                <th>Gast</th>
                <th>E-Mail</th>
                <th>Buchung</th>
                <th>Aufenthalt</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.recipients.map((row) => (
                <tr key={row.booking_id}>
                  <td className="font-medium">{row.guest_name}</td>
                  <td>{row.email}</td>
                  <td>
                    {row.booking_number}
                    {row.pitch_code ? ` · Platz ${row.pitch_code}` : ""}
                  </td>
                  <td>
                    {formatIsoDateShort(row.arrival_date)} – {formatIsoDateShort(row.departure_date)}
                  </td>
                  <td>{statusLabel(row.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {preview.sample_body_text ? (
        <Card title="So liest sich die Mail für den ersten Gast" description={preview.sample_to ? `An ${preview.sample_to}` : undefined}>
          <p className="text-sm font-semibold text-label">{preview.sample_subject}</p>
          <pre className="mt-3 whitespace-pre-wrap rounded-xl bg-fill-tertiary/40 p-4 text-sm leading-relaxed text-label">
            {preview.sample_body_text}
          </pre>
        </Card>
      ) : null}
    </div>
  );
}
